import { base64EncodeUrl } from '@/helpers/base64-helper';
import { TrackingAppId, TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { isClickoutOfferDetails } from '@/interfaces/snowplow/clickout-context';
import { SnowplowClickoutContextAffiliateLink } from '@/helpers/tracking/providers';

import type { SportType } from '@/@types/graphql-types';
import type { BuyboxParam } from '@/helpers/offer-helper';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { ClickoutOfferDetails } from '@/interfaces/snowplow/clickout-context';

type Maybe<T> = T | null | undefined;

const encodeContexts = (contexts: SnowplowContext[]): string => {
	const completeContext = {
		schema: 'iglu:com.snowplowanalytics.snowplow/contexts/jsonschema/1-0-0',
		data: contexts.map(c => c.toObject()),
	};

	let base64EncodedContext;
	try {
		const contextString = JSON.stringify(completeContext);
		base64EncodedContext = base64EncodeUrl(btoa(unescape(encodeURIComponent(contextString))));
	} catch (error) {
		console.error('[OfferHelper] encodeContexts: Failed to generate encoded context string: ', error);
		base64EncodedContext = '';
	}
	return base64EncodedContext;
};

type Offer = Maybe<{ standardWebURL?: Maybe<string> }>;
type ClickoutUrlParam =
	| 'r'
	| 'cx'
	| 'sid'
	| 'uct_buybox'
	| 'uct_country'
	| 'uct_ct'
	| 'uct_itsct'
	| 'uct_sport'
	| 'uct_tryout'
	| 'uct_web_app_version'
	| 'utm_medium'
	| 'utm_source';

export class ClickoutUrl {
	url: URL;

	/**
	 * @param offer Any object that, at least, has "standardWebURL"
	 * @param fallback URL to use if standardWebURL is falsy
	 */
	constructor(offer: Offer | (Offer & ClickoutOfferDetails), fallback = '') {
		this.url = new URL(`${JW_CONFIG.CLICK_URL}/a`);
		// adds app version information to the URL as a query parameter
		this.set('uct_web_app_version', TrackingAppId);

		// May need to be encoded due to click tags provided by clients
		this.set('r', offer?.standardWebURL ?? fallback);

		const clickoutContext = isClickoutOfferDetails(offer)
			? SnowplowClickoutContextAffiliateLink.fromProviderOffer(offer)
			: undefined;

		this.setContexts(clickoutContext);
	}

	/**
	 * Set a URL parameter, if the value is falsy, the parameter will not be added.
	 * Note that parameter values are automatically encoded.
	 */
	set(parameter: 'r', value: string): ClickoutUrl;
	set(parameter: 'cx', value: string): ClickoutUrl;
	/** Domain session ID */
	set(parameter: 'sid', value: Maybe<string>): ClickoutUrl;
	set(parameter: 'uct_buybox', value: BuyboxParam): ClickoutUrl;
	set(parameter: 'uct_country', value: string): ClickoutUrl;
	set(parameter: 'uct_ct', value: Maybe<string>): ClickoutUrl;
	set(parameter: 'uct_itsct', value: Maybe<string>): ClickoutUrl;
	set(parameter: 'uct_sport', value: Maybe<SportType>): ClickoutUrl;
	set(parameter: 'uct_tryout', value: number): ClickoutUrl;
	set(parameter: 'utm_medium', value: string): ClickoutUrl;
	set(parameter: 'utm_source', value: string): ClickoutUrl;
	set(parameter: 'uct_web_app_version', value: string): ClickoutUrl;
	set(parameter: ClickoutUrlParam, value: Maybe<string | number>): ClickoutUrl {
		if (value) {
			this.url.searchParams.set(parameter, value.toString());
		}

		return this;
	}

	/**
	 * Helper for setting additional contexts, already includes `TrackingHelper.getAdditionalContexts`
	 * @param contexts Single or array of SnowplowContexts to include in the URL
	 */
	setContexts(contexts: SnowplowContext | SnowplowContext[] = []) {
		const allContexts = TrackingHelper.getAdditionalContexts();
		Array.isArray(contexts) ? allContexts.concat(contexts) : allContexts.push(contexts);

		this.set('cx', encodeContexts(allContexts));

		return this;
	}

	toString() {
		return this.url.href;
	}
}
